/**
 * useUpdateSubscription — Mutation hook for the `update-subscription` CF
 * Worker handler (upgrade / downgrade / change_interval).
 *
 * - Upgrades: immediate proration (invoice now)
 * - Downgrades: deferred to next billing period
 * - Interval changes: immediate (Polar forces proration)
 *
 * DTO contract (2026-04-18):
 * Uses `fetchJsonValidated(UpdateSubscriptionResultDto, ...)` so server
 * responses are validated against the shared contract. Existing callsites
 * (`subscription.tsx`, `useBillingIntervalChange`) use raw `fetch()` today;
 * this hook gives new call sites the typed, validated surface.
 *
 * Wave 4B (DTO migration): created alongside the handler DTO migration. The
 * existing direct-`fetch()` call sites remain intact for now and can migrate
 * to this hook incrementally.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { fetchJsonValidated } from '@dramadetector/api-client';
import {
  UpdateSubscriptionResultDto,
  type UpdateSubscriptionResult,
  type UpdateSubscriptionAction,
} from '@dramadetector/shared';
import { getEdgeFunctionUrl, getAuthHeaders } from '../lib/api-url';
import { subscriptionKeys } from './useSubscription';

export interface UpdateSubscriptionArgs {
  /** Type of change to apply. */
  action: UpdateSubscriptionAction;
  /** Polar product key to transition to (e.g. 'dramax_monthly'). */
  target_product_key: string;
}

export interface UseUpdateSubscriptionReturn {
  /** Trigger update — resolves with the DTO, rejects on error. */
  updateSubscription: (args: UpdateSubscriptionArgs) => Promise<UpdateSubscriptionResult>;
  /** True while the mutation is in-flight. */
  isUpdating: boolean;
  /** Last error, or null. */
  error: Error | null;
}

export function useUpdateSubscription(): UseUpdateSubscriptionReturn {
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (args: UpdateSubscriptionArgs): Promise<UpdateSubscriptionResult> => {
      if (!accessToken) throw new Error('Not authenticated');

      return await fetchJsonValidated(
        UpdateSubscriptionResultDto,
        getEdgeFunctionUrl('update-subscription'),
        {
          method: 'POST',
          headers: { ...getAuthHeaders(accessToken) },
          body: JSON.stringify(args),
        },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
    },
  });

  return {
    updateSubscription: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error as Error | null,
  };
}
